/**
 * AI helpers for blog article generation.
 *
 * Distinct from ai.ts (which is for CMS full-page HTML).
 * Returns structured content: title, excerpt, body (HTML), tags.
 * Uses the same OpenAI / Anthropic fetch patterns as ai.ts.
 */

import type { AIConfig } from './cms';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BlogGenerateParams {
  config: AIConfig;
  articleContext: string;
  businessContextText?: string;
  /** When set and provider is OpenAI, use Responses API with tools: [{ type: 'web_search' }]. See docs/OpenAI-refs/responses-api-create.md */
  openAIWebSearchQuery?: string;
}

export interface GeneratedBlogArticle {
  title: string;
  excerpt: string;
  body: string;
  tags: string[];
  /** When present, from web search response; per websearch.md inline citations must be visible and clickable. */
  citations?: WebSearchCitation[];
}

/** Per docs/OpenAI-refs/websearch.md: url_citation in message.content[].annotations. */
export interface WebSearchCitation {
  url: string;
  title?: string;
  start_index?: number;
  end_index?: number;
}

// ---------------------------------------------------------------------------
// System prompt builder
// ---------------------------------------------------------------------------

function buildSystemPrompt(businessContextText: string): string {
  const businessSection = businessContextText.trim()
    ? `\n\n## Business Context about SMBPrime\n\nAlways incorporate the following knowledge about SMBPrime where relevant. Do not contradict it.\n\n${businessContextText}`
    : '';

  return `
You are an expert blog writer for SMBPrime, a federal government contracting advisory and business development platform for small and mid-size businesses (SMBs).

## Audience
Federal contractors, small business owners, GovCon newcomers, BD professionals. Write with authority, clarity, and a trusted-advisor tone. Never be generic or salesy.

## Brand Voice
- Professional and authoritative
- Practical and actionable — give readers real, specific guidance
- Reference real GovCon concepts when relevant: NAICS codes, SAM.gov, BD pipeline, teaming, set-asides, 8(a), HUBZone, SDVOSB, CAGE codes, UEI, past performance, CPARS, GSA schedules
- Avoid vague marketing speak${businessSection}

## Output Format
You MUST output ONLY a valid JSON object — nothing else before or after it.

The JSON object must have exactly these keys:
- "title": string — compelling, SEO-friendly article title (max 80 chars)
- "excerpt": string — 1–2 sentence summary for meta description and article cards (max 200 chars)
- "body": string — full article body as HTML (see HTML rules below)
- "tags": array of strings — 3–6 relevant keyword tags (lowercase, no #, e.g. ["sam.gov", "naics codes", "small business"])

## HTML Rules for "body"
- Use semantic HTML: <p>, <h2>, <h3>, <ul>, <ol>, <li>, <strong>, <em>, <a>
- Do NOT use <h1> (the page already renders the title as H1)
- Do NOT include <html>, <head>, <body>, <article>, or any wrapper tags
- Do NOT use Tailwind classes or inline styles — the body is rendered in a styled article context
- Start with a short lead paragraph summarizing the key takeaway
- Structure with H2 sections and bullet lists for scannability
- Minimum 400 words, aim for 600–900 words for depth
- Hyperlinks in body should use placeholder hrefs like "#" unless the URL is known and verified
`.trim();
}

// ---------------------------------------------------------------------------
// User prompt builder
// ---------------------------------------------------------------------------

function buildUserPrompt(params: BlogGenerateParams): string {
  const parts: string[] = [];

  if (params.openAIWebSearchQuery?.trim()) {
    parts.push(
      `First search the web for current, verified information about: ${params.openAIWebSearchQuery.trim()}. Use what you find to inform the article. Cite sources in the body with real URLs so citations are visible and clickable (e.g. <a href="https://...">...</a>). Then write the blog article below.`
    );
  }

  parts.push(`Write a blog article about:\n${params.articleContext.trim()}`);

  parts.push(
    'Remember: output ONLY the raw JSON object — no markdown fences, no explanation.'
  );

  return parts.join('\n\n');
}

// ---------------------------------------------------------------------------
// JSON parsing
// ---------------------------------------------------------------------------

/**
 * Normalize the article body HTML from AI responses so it renders correctly.
 * - Replaces literal backslash-n (e.g. from double-escaped JSON) with real newlines.
 * - Replaces literal backslash-quote with quote so href="..." in links are valid.
 * - Trims leading/trailing whitespace and normalizes line endings for consistent display.
 */
function normalizeArticleBody(body: string): string {
  if (!body) return '';
  let out = body
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  // Literal \n (backslash + n) that sometimes appear in model output → real newline
  out = out.replace(/\\n/g, '\n');
  // Literal \" (backslash + quote) inside attributes → single quote so HTML is valid
  out = out.replace(/\\"/g, '"');
  return out.trim();
}

/**
 * If the model hit max_output_tokens, the JSON may be truncated (e.g. mid-"body").
 * Try to close the object so we can salvage title, excerpt, and partial body.
 */
function tryRepairTruncatedArticleJson(jsonStr: string): Record<string, unknown> | null {
  const candidates = [
    jsonStr + '","tags":[]}',   // truncation after body string content
    jsonStr + '"}]}',          // truncation inside tags array
    jsonStr + '"}',            // truncation at end of body value
  ];
  for (const s of candidates) {
    try {
      const parsed = JSON.parse(s) as Record<string, unknown>;
      if (typeof parsed.title === 'string' && typeof parsed.body === 'string') return parsed;
    } catch {
      // continue
    }
  }
  return null;
}

function parseGeneratedArticle(raw: string): GeneratedBlogArticle {
  const trimmed = raw.trim();

  // Strip markdown code fences if model added them despite instructions
  const fenceMatch = trimmed.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```\s*$/);
  const jsonStr = fenceMatch ? fenceMatch[1].trim() : trimmed;

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(jsonStr) as Record<string, unknown>;
  } catch {
    // Truncation (e.g. max_output_tokens): try closing open "body" string and object
    const repaired = tryRepairTruncatedArticleJson(jsonStr);
    if (repaired) {
      parsed = repaired;
    } else {
      const objMatch = jsonStr.match(/\{[\s\S]*\}/);
      if (objMatch) {
        try {
          parsed = JSON.parse(objMatch[0]) as Record<string, unknown>;
        } catch {
          throw new Error(
            '[ai-blog] Could not parse JSON from model response (may be truncated by token limit). Response preview: ' +
              trimmed.slice(0, 200)
          );
        }
      } else {
        throw new Error(
          '[ai-blog] Model did not return valid JSON (may be truncated by token limit). Response preview: ' +
            trimmed.slice(0, 200)
        );
      }
    }
  }

  const title = typeof parsed.title === 'string' ? parsed.title.trim() : '';
  const excerpt = typeof parsed.excerpt === 'string' ? parsed.excerpt.trim() : '';
  const rawBody = typeof parsed.body === 'string' ? parsed.body.trim() : '';
  const body = normalizeArticleBody(rawBody);
  const rawTags = Array.isArray(parsed.tags) ? parsed.tags : [];
  const tags = rawTags
    .filter((t): t is string => typeof t === 'string')
    .map((t) => t.toLowerCase().trim())
    .filter(Boolean);

  if (!title || !body) {
    throw new Error(
      '[ai-blog] Model returned incomplete article (missing title or body).'
    );
  }

  return { title, excerpt, body, tags };
}

// ---------------------------------------------------------------------------
// Provider calls
// ---------------------------------------------------------------------------

/** Models that support the Responses API web search tool (web_search_preview). */
const OPENAI_WEB_SEARCH_MODELS = [
  'gpt-5.4',
  'gpt-5-mini',
  'gpt-4o-search-preview',
  'gpt-4o-search-preview-2025-03-11',
  'gpt-4o-mini-search-preview',
  'gpt-4o-mini-search-preview-2025-03-11',
  'gpt-5-search-api',
];

/** Legacy undated search model IDs that often return "Model not found"; map to dated IDs for Requests API. */
const OPENAI_SEARCH_MODEL_ALIASES: Record<string, string> = {
  'gpt-4o-search-preview': 'gpt-4o-search-preview-2025-03-11',
  'gpt-4o-mini-search-preview': 'gpt-4o-mini-search-preview-2025-03-11',
};

/** Resolve OpenAI model from workflow config. Prefer assigned model; map legacy search IDs to dated IDs; fallback to allowed gpt-5-mini. */
function resolveOpenAIModel(config: AIConfig): string {
  const raw = config.model?.trim();
  if (!raw || raw === 'gpt-5-mini-2025-08-07') return 'gpt-5-mini';
  return OPENAI_SEARCH_MODEL_ALIASES[raw] ?? raw;
}

const OPENAI_RESPONSES_API_URL = 'https://api.openai.com/v1/responses';

/** Per websearch.md: url_citation in message.content[].annotations. */
interface UrlCitationAnnotation {
  type: string;
  url?: string;
  title?: string;
  start_index?: number;
  end_index?: number;
}

/** Response shape from OpenAI Responses API (POST /responses). @see docs/OpenAI-refs/responses-api-create.md, websearch.md */
interface ResponsesApiOutputPart {
  type: string;
  text?: string;
  refusal?: string;
  annotations?: UrlCitationAnnotation[];
}
interface ResponsesApiOutputItem {
  type: string;
  content?: ResponsesApiOutputPart[];
}
interface ResponsesApiResponse {
  error?: { message?: string; code?: string };
  output?: ResponsesApiOutputItem[];
  status?: string;
  incomplete_details?: { reason?: string };
}

/** Extract assistant text from Responses API response.output array. */
function extractTextFromResponsesOutput(output: ResponsesApiOutputItem[] | undefined): string {
  const { text } = extractTextAndCitationsFromResponsesOutput(output);
  return text;
}

/**
 * Extract text and url_citation annotations from Responses API output (web search).
 * The article output lives at: response.output[] → item with type === "message" → content[] → part with type === "output_text" → part.text.
 * (output also contains reasoning and web_search_call items; we only use the message's output_text for the article JSON.)
 * Per websearch.md: message.content[].text and message.content[].annotations (url_citation) must be visible/clickable.
 */
function extractTextAndCitationsFromResponsesOutput(
  output: ResponsesApiOutputItem[] | undefined
): { text: string; citations: WebSearchCitation[] } {
  let text = '';
  const citations: WebSearchCitation[] = [];
  if (!Array.isArray(output)) return { text, citations };
  for (const item of output) {
    if (item.type !== 'message' || !Array.isArray(item.content)) continue;
    for (const part of item.content) {
      if (part.type === 'output_text') {
        if (typeof part.text === 'string') text += part.text;
        const ann = part.annotations;
        if (Array.isArray(ann)) {
          for (const a of ann) {
            if (a.type === 'url_citation' && a.url) {
              citations.push({
                url: a.url,
                title: a.title,
                start_index: a.start_index,
                end_index: a.end_index,
              });
            }
          }
        }
      }
    }
  }
  return { text, citations };
}

/**
 * Call OpenAI via the Responses API (POST /v1/responses).
 * Uses instructions (system), input (user), max_output_tokens, and text.format per docs.
 * @see docs/OpenAI-refs/responses-api-create.md, responses-api-documentation.md
 */
async function callOpenAIResponses(
  config: AIConfig,
  systemPrompt: string,
  userPrompt: string,
  options: { maxOutputTokens: number; jsonMode?: boolean; tools?: Array<{ type: string }> }
): Promise<string> {
  const model = resolveOpenAIModel(config);
  const body: Record<string, unknown> = {
    model,
    instructions: systemPrompt,
    input: userPrompt,
    max_output_tokens: options.maxOutputTokens,
    store: false,
    text: {
      format: options.jsonMode ? { type: 'json_object' as const } : { type: 'text' as const },
    },
  };
  if (options.tools?.length) body.tools = options.tools;

  const response = await fetch(OPENAI_RESPONSES_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify(body),
  });

  const data = (await response.json()) as ResponsesApiResponse;
  if (!response.ok) {
    const msg = (data.error?.message ?? (await response.text().catch(() => ''))) || response.statusText;
    throw new Error(`[ai-blog] OpenAI Responses API failed (${response.status}): ${msg}`);
  }
  if (data.error?.message) throw new Error(`[ai-blog] OpenAI error: ${data.error.message}`);

  return extractTextFromResponsesOutput(data.output) || '';
}

async function callOpenAI(
  config: AIConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  return callOpenAIResponses(config, systemPrompt, userPrompt, {
    maxOutputTokens: 4096,
    jsonMode: true,
  });
}

/** OpenAI plain-text (e.g. lucky article context) via Responses API. */
async function callOpenAIPlainText(
  config: AIConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  return callOpenAIResponses(config, systemPrompt, userPrompt, {
    maxOutputTokens: 1024,
    jsonMode: false,
  });
}

/**
 * OpenAI Responses API with web search tool.
 * Matches official sample: POST /v1/responses with tools: [{ type: "web_search_preview" }].
 * Web Search cannot be used with JSON mode (API restriction), so we use text format
 * and rely on the system prompt + parseGeneratedArticle to extract the JSON object.
 * Response includes web_search_call items and message content with optional url_citation annotations.
 * @see docs/OpenAI-refs/responses-api-create.md, docs/OpenAI-refs/websearch.md
 */
async function callOpenAIWithWebSearch(
  config: AIConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<{ text: string; citations: WebSearchCitation[] }> {
  const resolved = resolveOpenAIModel(config);
  const model = OPENAI_WEB_SEARCH_MODELS.includes(resolved) ? resolved : 'gpt-4o-mini-search-preview';

  const body: Record<string, unknown> = {
    model,
    instructions: systemPrompt,
    input: userPrompt,
    max_output_tokens: 8192, // 4096 was too low for web-search + long article (reasoning + message); avoid incomplete/max_output_tokens
    store: false,
    text: { format: { type: 'text' } }, // JSON mode not allowed with web search; prompt requires JSON object
    tools: [{ type: 'web_search_preview' }], // per official sample: curl .../v1/responses -d '{"model":"gpt-5.4","tools":[{"type":"web_search_preview"}],"input":"..."}'
  };

  const response = await fetch(OPENAI_RESPONSES_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify(body),
  });

  const data = (await response.json()) as ResponsesApiResponse;
  if (!response.ok) {
    const msg = (data.error?.message ?? (await response.text().catch(() => ''))) || response.statusText;
    throw new Error(
      `[ai-blog] OpenAI web search (Responses API) failed (${response.status}): ${msg}`
    );
  }
  if (data.error?.message) throw new Error(`[ai-blog] OpenAI error: ${data.error.message}`);

  const incompleteReason = data.incomplete_details?.reason;
  if (data.status === 'incomplete' && incompleteReason === 'max_output_tokens') {
    throw new Error(
      '[ai-blog] Response was cut off (max_output_tokens). The article was too long; try a shorter topic or try again (we use a higher limit now).'
    );
  }

  const { text, citations } = extractTextAndCitationsFromResponsesOutput(data.output);
  return { text: text || '', citations };
}

async function callAnthropic(
  config: AIConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: config.model || 'claude-sonnet-4-5',
      system: systemPrompt,
      messages: [{ role: 'user', content: userPrompt }],
      max_completion_tokens: 4096,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `[ai-blog] Anthropic request failed (${response.status}): ${errorBody || response.statusText}`
    );
  }

  const data = (await response.json()) as {
    content?: Array<{ type: string; text?: string }>;
  };
  return data.content?.find((b) => b.type === 'text')?.text ?? '';
}

async function callGemini(
  config: AIConfig,
  systemPrompt: string,
  userPrompt: string
): Promise<string> {
  const model = config.model || 'gemini-1.5-pro';
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-goog-api-key': config.apiKey,
    },
    body: JSON.stringify({
      systemInstruction: { parts: [{ text: systemPrompt }] },
      contents: [{ parts: [{ text: userPrompt }] }],
      generationConfig: {
        temperature: 0.7,
        maxOutputTokens: 4096,
      },
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `[ai-blog] Gemini request failed (${response.status}): ${errorBody || response.statusText}`
    );
  }

  const data = (await response.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
  };
  return data.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate a structured blog article (title, excerpt, body HTML, tags) using
 * the configured AI provider.
 *
 * @param params.config        - AI provider config (from Firestore settings/aiConfig)
 * @param params.articleContext - Admin-provided description of what the article is about
 * @param params.businessContextText - Concatenated business context library items
 * @param params.searchContext  - Optional formatted web search results to ground the article
 */
export async function generateBlogArticleWithAI(
  params: BlogGenerateParams
): Promise<GeneratedBlogArticle> {
  const systemPrompt = buildSystemPrompt(params.businessContextText ?? '');
  const userPrompt = buildUserPrompt(params);

  let raw: string;
  let citations: WebSearchCitation[] | undefined;
  if (params.config.provider === 'openai') {
    if (params.openAIWebSearchQuery?.trim()) {
      const out = await callOpenAIWithWebSearch(
        params.config,
        systemPrompt,
        userPrompt
      );
      raw = out.text;
      citations = out.citations.length ? out.citations : undefined;
    } else {
      raw = await callOpenAI(params.config, systemPrompt, userPrompt);
    }
  } else if (params.config.provider === 'anthropic') {
    raw = await callAnthropic(params.config, systemPrompt, userPrompt);
  } else if (params.config.provider === 'gemini') {
    raw = await callGemini(params.config, systemPrompt, userPrompt);
  } else {
    throw new Error(`[ai-blog] Unknown AI provider: ${params.config.provider as string}`);
  }

  const article = parseGeneratedArticle(raw);
  if (citations?.length) article.citations = citations;
  return article;
}

// ---------------------------------------------------------------------------
// Lucky article context (Make me lucky)
// ---------------------------------------------------------------------------

const LUCKY_CONTEXT_SYSTEM_PROMPT = `You are helping SMBPrime editors write Insights-style blog articles. The Insights page promises: "Practical insights on federal subcontracting, Prime outreach, and structured growth."

Topics we cover: Federal Subcontracting Strategy, Prime Contractor Outreach, BD Process & Systems, Small Business Growth, NAICS Alignment, Meeting Preparation.

Every article must include: (1) Practical Insights — real strategies readers can apply immediately; (2) Growth Focus — structured, sustainable growth, not quick wins; (3) Clear Next Steps — a direct path forward (e.g. our process or a strategy call).

Your task: suggest a brief "article context" (one short paragraph or a few lines) that will be used to generate a full blog post. Include: topic (from or aligned with the six topics above), target audience (SMBs, BD/capture roles), key points, and tone (professional, authoritative, trusted advisor). Output ONLY plain text — no JSON, markdown headers, or code.`;

/**
 * Generate a suggested article context string using the AI provider (for "Make me lucky").
 * Uses short business context so the suggestion is on-brand.
 * When topic is provided, the AI is instructed to use that topic (caller picks randomly).
 */
export async function generateLuckyArticleContext(params: {
  config: AIConfig;
  businessContextSummary: string;
  /** When set, the AI must use this topic (ensures random topic from "Make me lucky" is used). */
  topic?: string;
}): Promise<string> {
  const { config, businessContextSummary, topic } = params;
  const topicInstruction = topic?.trim()
    ? `Use this topic: ${topic.trim()}. `
    : '';
  const userPrompt = businessContextSummary.trim()
    ? `Based on this short context about the business:\n\n${businessContextSummary.trim()}\n\n${topicInstruction}Generate a brief article context suggestion for an Insights-style SMBPrime blog post. Ensure the suggestion implies: practical insights (real strategies), growth focus (sustainable, not quick wins), and clear next steps. Include target audience and tone. Plain text only.`
    : `${topicInstruction}Generate a brief article context suggestion for an Insights-style SMBPrime blog post (practical insights on federal subcontracting, Prime outreach, structured growth). Include practical insights, growth focus, clear next steps, target audience, and tone. Plain text only.`;

  if (config.provider === 'openai') {
    return callOpenAIPlainText(config, LUCKY_CONTEXT_SYSTEM_PROMPT, userPrompt);
  }
  if (config.provider === 'anthropic') {
    return callAnthropic(config, LUCKY_CONTEXT_SYSTEM_PROMPT, userPrompt);
  }
  if (config.provider === 'gemini') {
    return callGemini(config, LUCKY_CONTEXT_SYSTEM_PROMPT, userPrompt);
  }
  throw new Error(`[ai-blog] Unknown AI provider: ${config.provider as string}`);
}
